/**
 * Detection and recovery policy for build-asset ("chunk") load failures.
 *
 * Next.js code-splits the app into hashed `/_next/static/` chunks that a tab
 * fetches lazily — the first time it navigates to a route or renders a
 * `lazy()` component. A container-app revision only serves its own build
 * output, so every deploy strands the chunks belonging to the build that
 * already-open tabs are still running. Such a tab keeps working until it
 * reaches for a chunk it never downloaded; the request then returns an HTML
 * error page, which the browser reports as a script parse error, a failed
 * resource load, or a rejected `import()` depending on how the chunk was
 * requested.
 *
 * The tab cannot recover on its own: React's `lazy()` caches the rejected
 * import forever, so re-rendering the same component fails without even
 * retrying the network. Only a reload onto the current build fixes it.
 *
 * This module is pure so the classification and the reload/prompt decision
 * are unit-testable; the DOM plumbing lives in `<ChunkReloadGuard>`.
 */

/** Normalized view of the three browser events a stranded chunk can produce. */
export interface ChunkFailureSignal {
  /** Error text, when the failure surfaced as a thrown error. */
  message?: string;
  /** `Error.name`, e.g. the bundler's `ChunkLoadError`. */
  name?: string;
  /** Set for a failed `<script>`/`<link>`, which carries no message at all. */
  resource?: boolean;
  /** URL of the asset that failed, when the browser reported one. */
  url?: string;
}

export type ChunkRecovery = "reload" | "prompt";

/**
 * How long an auto-reload suppresses the next one. A reload that lands on the
 * same broken build would otherwise fail identically and reload again, so the
 * second failure within the window falls back to asking the user. Long enough
 * to break that loop, short enough that a tab left open across two deploys
 * still self-heals the second time.
 */
export const CHUNK_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

/** Session-scoped so the marker survives the reload it is guarding. */
export const CHUNK_RELOAD_STORAGE_KEY = "magnus:chunk-reload-at";

const BUILD_ASSET_PATH = "/_next/static/";

/** Bundler error for a dynamic `import()` whose chunk could not be fetched. */
const CHUNK_ERROR_NAME = "ChunkLoadError";

/**
 * Messages that only a chunk fetch produces, so they need no corroborating
 * URL. Covers webpack's loader, native ESM, and the module-script MIME check
 * that rejects an HTML error page before it is ever parsed.
 */
const CHUNK_MESSAGE_PATTERNS: readonly RegExp[] = [
  /loading (?:css )?chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /mime type of "text\/html"/i,
];

/**
 * An HTML error page parsed as JavaScript. Chrome and Safari report
 * "Unexpected token '<'", Firefox "expected expression, got '<'". These are
 * generic `SyntaxError` texts, so they only count against a build asset.
 */
const HTML_AS_SCRIPT_PATTERNS: readonly RegExp[] = [
  /unexpected token '?</i,
  /expected expression, got '?</i,
];

/**
 * `JSON.parse` of an HTML response reports the same "Unexpected token '<'",
 * and its `filename` is whichever app chunk was running — a build asset. It
 * is an ordinary bug that a reload won't fix, so it must not be mistaken for
 * a stranded chunk.
 */
const NOT_A_CHUNK_PATTERNS: readonly RegExp[] = [/is not valid json/i];

/** Whether a URL points at this app's immutable build output. */
export function isBuildAssetUrl(url: string | undefined | null): boolean {
  if (!url) {
    return false;
  }
  // Match on the path so an absolute URL (a CDN `assetPrefix`) is recognized
  // the same way a root-relative one is.
  const path = url.split(/[?#]/)[0] ?? "";
  return path.includes(BUILD_ASSET_PATH);
}

/** Whether a browser error signal is a stranded build asset. */
export function isChunkLoadFailure(signal: ChunkFailureSignal): boolean {
  const message = signal.message ?? "";

  if (NOT_A_CHUNK_PATTERNS.some((pattern) => pattern.test(message))) {
    return false;
  }
  if (signal.name === CHUNK_ERROR_NAME) {
    return true;
  }
  if (CHUNK_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))) {
    return true;
  }
  // A failed `<script>`/`<link>` reports no message, so the asset URL is the
  // only evidence there is.
  if (signal.resource) {
    return isBuildAssetUrl(signal.url);
  }
  return (
    isBuildAssetUrl(signal.url) &&
    HTML_AS_SCRIPT_PATTERNS.some((pattern) => pattern.test(message))
  );
}

/**
 * Whether to reload straight away or ask first. Reloading is the real fix, so
 * it is the default; prompting is for the cases where reloading unprompted
 * would either destroy the user's work or risk a loop.
 */
export function decideChunkRecovery({
  lastReloadAt,
  now,
  hasUnsavedInput,
}: {
  /** Timestamp of this tab's previous auto-reload, or `null` if there was none. */
  lastReloadAt: number | null;
  now: number;
  /** Whether reloading would discard something the user typed. */
  hasUnsavedInput: boolean;
}): ChunkRecovery {
  if (hasUnsavedInput) {
    return "prompt";
  }
  if (lastReloadAt === null) {
    return "reload";
  }
  // A timestamp in the future means the clock moved; treat it as recent
  // rather than trusting it enough to reload again.
  const elapsed = now - lastReloadAt;
  return elapsed >= 0 && elapsed >= CHUNK_RELOAD_COOLDOWN_MS
    ? "reload"
    : "prompt";
}

/** `sessionStorage`, or `null` where the browser refuses to hand it over. */
export function getSessionStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    // Blocked by cookie policy in some embedded contexts.
    return null;
  }
}

/** Reads this tab's last auto-reload timestamp. */
export function readLastChunkReload(storage: Storage | null): number | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Records an auto-reload. Returns whether the marker actually persisted — the
 * caller must not reload when it didn't, because an unrecorded reload leaves
 * nothing to stop the next failure from reloading again.
 */
export function recordChunkReload(
  storage: Storage | null,
  at: number
): boolean {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(at));
    return true;
  } catch {
    return false;
  }
}
